import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import {
  AssignmentTurnedIn as OnboardingIcon,
  AccountBalance as AccountBalanceIcon,
  Check as CheckIcon,
  DeviceHub as DeviceHubIcon,
  LocalMall as LocalMallIcon,
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  People as PeopleIcon,
  Receipt as ReceiptIcon,
  Store as StoreIcon,
  Storefront as StorefrontIcon,
  TableChart as TableChartIcon,
  UnfoldMore as UnfoldMoreIcon,
  ViewModule as ViewModuleIcon,
} from '@material-ui/icons';

import { PINPEO } from 'theme/palette';
import { useAggregator } from 'context/AggregatorContext';
import { hasPermissions } from 'utils/permissions';
import { getSettings } from 'utils/settings';
import { firstLetter } from 'utils/strings';
import { isModuleVisible, isPosEnabled } from 'utils/moduleVisibility';
import { onboardingAccess } from 'services/onboarding';

import AccountSection from './AccountSection';

export const SIDEBAR_WIDTH = 236;
export const SIDEBAR_WIDTH_COLLAPSED = 64;

// Navegación del rediseño en 2 secciones. Mismos módulos/permisos que antes.
const NAV_SECTIONS = [
  {
    subheader: 'Operación',
    items: [
      { href: '/transactions', icon: ViewModuleIcon, module: 'transactions', title: 'Transacciones' },
      { href: '/commerce', icon: StorefrontIcon, module: 'commerce', title: 'Comercios' },
      // Solo en las marcas que venden punto de venta (`enabled` se evalúa en
      // render: a nivel de módulo la config del dominio puede no estar todavía).
      { enabled: isPosEnabled, href: '/pos', icon: LocalMallIcon, module: 'pos', title: 'Punto de venta' },
      { href: '/', icon: ReceiptIcon, module: 'terminals', title: 'Terminales' },
    ],
  },
  {
    subheader: 'Gestión',
    items: [
      { href: '/reports', icon: TableChartIcon, module: 'reports', title: 'Reportes' },
      { href: '/liquidation', icon: AccountBalanceIcon, module: 'liquidation', title: 'Liquidación' },
      { href: '/users', icon: PeopleIcon, module: 'users', title: 'Usuarios' },
      { href: '/aggregator', icon: StoreIcon, module: 'aggregator', title: 'Agregadores' },
      // Sin `module`: no usa el sistema de roles del panel. Se muestra solo a
      // los revisores, y eso lo decide el gateway con sus grupos de Cognito
      // (ver `onboardingAccess`); el permiso real lo vuelve a aplicar en cada
      // llamada.
      { href: '/onboarding', icon: OnboardingIcon, title: 'Onboarding', reviewersOnly: true },
    ],
  },
];

// Mismo filtrado por visibilidad + permisos que usaba el NavBar anterior
const filterNavItems = (items) =>
  items.filter((item) => {
    // Items que solo existen en algunas marcas.
    if (typeof item.enabled === 'function' && !item.enabled()) return false;
    if ([null, undefined, ''].includes(item.module)) return true;
    if (!isModuleVisible(item.module)) return false;

    const permissionName = Array.isArray(item.module)
      ? item.module.map((m) => `${m}.select.list${firstLetter(m)}`)
      : `${item.module}.select.list${firstLetter(item.module)}`;

    return hasPermissions(permissionName, true);
  });

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    // Color oscuro de marca (settings.colors.brandDark del JSON de dominio)
    backgroundColor: theme.brand.brandDark,
    transition: 'width .18s ease',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 14px 12px',
    flexShrink: 0,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minWidth: 0,
  },
  brandMark: {
    width: 30,
    height: 30,
    borderRadius: 9,
    background: theme.brand.primary,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 17,
    flexShrink: 0,
  },
  brandName: {
    color: '#fff',
    fontSize: 19,
    fontWeight: 700,
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap',
  },
  brandLogo: {
    display: 'block',
    width: 154,
    height: 36,
    objectFit: 'contain',
    objectPosition: 'left center',
  },
  brandLogoPinpeo: {
    width: 174,
    height: 42,
  },
  brandIcon: {
    display: 'block',
    width: 30,
    height: 30,
    objectFit: 'contain',
    flexShrink: 0,
  },
  // Cuando el tenant no tiene isotipo cuadrado se reusa el logo horizontal
  // dentro del riel de 64px, por eso es mas ancho y bajo que brandIcon.
  brandIconWide: {
    width: 44,
    height: 24,
  },
  collapseButton: {
    color: 'rgba(255,255,255,.6)',
    padding: 6,
    '&:hover': {
      color: '#fff',
      backgroundColor: 'rgba(255,255,255,.10)',
    },
  },
  selectorSection: {
    padding: '4px 14px 14px',
    flexShrink: 0,
  },
  selectorLabel: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'rgba(255,255,255,.4)',
    marginBottom: 6,
  },
  selectorButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,.08)',
    border: '1px solid rgba(255,255,255,.16)',
    borderRadius: 10,
    padding: '9px 10px',
    cursor: 'pointer',
    color: '#fff',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    textAlign: 'left',
    '&:hover': {
      background: 'rgba(255,255,255,.12)',
    },
  },
  selectorButtonCollapsed: {
    justifyContent: 'center',
    padding: 9,
  },
  selectorName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nav: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '0 14px',
  },
  subheader: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'rgba(255,255,255,.4)',
    margin: '14px 0 6px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 10px',
    borderRadius: 9,
    color: 'rgba(255,255,255,.72)',
    fontSize: 13.5,
    fontWeight: 600,
    textDecoration: 'none',
    marginBottom: 2,
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'rgba(255,255,255,.10)',
      color: '#fff',
      textDecoration: 'none',
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    padding: 9,
  },
  navItemActive: {
    background: theme.brand.primary,
    color: '#fff',
    '&:hover': {
      background: theme.brand.primary,
      color: '#fff',
    },
  },
  navIcon: {
    fontSize: 20,
    flexShrink: 0,
  },
}));

/**
 * Sidebar único del rediseño: marca + selector global de agregador +
 * navegación (Operación/Gestión) + cuenta. Colapsable 236↔64px.
 */
function Sidebar({ collapsed, onToggleCollapse }) {
  const classes = useStyles();
  const theme = useTheme();
  const location = useLocation();

  // Identidad de marca desde el JSON del dominio (white-label).
  // Variante `-dark` porque el sidebar es una superficie oscura;
  // fallback: inicial + nombre de `company.name`.
  const brandLogo = getSettings('logo.imagotipo-horizontal-dark');
  const brandIsotype = getSettings('logo.imagotipo');
  // El favicon (`site.favicon`) es cuadrado por definicion y todos los
  // tenants lo traen, asi que es la mejor marca para el riel colapsado.
  const brandFavicon = getSettings('site.favicon');
  const companyName = getSettings('company.name', '');
  const brandInitial = firstLetter(companyName) || '·';
  const isPinpeo = companyName.trim().toLowerCase() === 'pinpeo';

  const {
    aggregators,
    selectedAggregator,
    selectedAggregatorId,
    setSelectedAggregatorId,
    selectorOpenRequest,
  } = useAggregator();

  const selectorRef = useRef(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  // ¿Es revisor del onboarding? Hasta saberlo, el ítem no se muestra.
  const [isReviewer, setIsReviewer] = useState(false);
  useEffect(() => {
    let alive = true;
    onboardingAccess().then((access) => { if (alive) setIsReviewer(Boolean(access)); });
    return () => { alive = false; };
  }, []);
  const [selectorSearch, setSelectorSearch] = useState('');
  // Estados de error independientes: el isotipo (colapsado) y el logo
  // horizontal (expandido) son archivos distintos y uno puede fallar sin
  // que el otro sea invalido. Se resetean si cambia la URL del JSON.
  const [faviconError, setFaviconError] = useState(false);
  const [isotypeError, setIsotypeError] = useState(false);
  const [brandLogoError, setBrandLogoError] = useState(false);

  useEffect(() => { setFaviconError(false); }, [brandFavicon]);
  useEffect(() => { setIsotypeError(false); }, [brandIsotype]);
  useEffect(() => { setBrandLogoError(false); }, [brandLogo]);

  // El botón "Elegir agregador" de los estados vacíos solicita abrir
  // el selector desde el contexto.
  useEffect(() => {
    if (selectorOpenRequest > 0) {
      setSelectorOpen(true);
    }
  }, [selectorOpenRequest]);

  const handleCloseSelector = () => {
    setSelectorOpen(false);
    setSelectorSearch('');
  };

  // Búsqueda por nombre o id dentro del dropdown
  const filteredAggregators = useMemo(() => {
    const q = selectorSearch.trim().toLowerCase();
    if (!q) return aggregators;

    return aggregators.filter(
      (item) =>
        (item.name || '').toLowerCase().includes(q) ||
        String(item.id).includes(q),
    );
  }, [aggregators, selectorSearch]);

  // Marca del riel colapsado, en cascada: favicon -> isotipo -> logo horizontal
  // (mismo archivo que ya se ve expandido) -> inicial de `company.name`.
  // Sin esta cascada, un `logo.imagotipo` inexistente dejaba el riel sin marca.
  let collapsedLogo = null;
  if (brandFavicon && !faviconError) {
    collapsedLogo = {
      src: brandFavicon,
      wide: false,
      onError: () => setFaviconError(true),
    };
  }
  else if (brandIsotype && !isotypeError) {
    collapsedLogo = {
      src: brandIsotype,
      wide: false,
      onError: () => setIsotypeError(true),
    };
  }
  else if (brandLogo && !brandLogoError) {
    collapsedLogo = {
      src: brandLogo,
      wide: true,
      onError: () => setBrandLogoError(true),
    };
  }

  const currentPath = location.pathname + location.search;

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = currentPath === item.href;

    const link = (
      <RouterLink
        key={item.title}
        to={item.href}
        exact
        className={clsx(
          classes.navItem,
          collapsed && classes.navItemCollapsed,
          active && classes.navItemActive,
        )}
      >
        <Icon className={classes.navIcon} />
        {!collapsed && <span>{item.title}</span>}
      </RouterLink>
    );

    return collapsed
      ? (
        <Tooltip key={item.title} title={item.title} placement="right">
          {link}
        </Tooltip>
      )
      : link;
  };

  return (
    <Box
      component="aside"
      className={classes.root}
      style={{ width: collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH }}
    >
      {/* Marca + colapso (identidad desde el JSON del dominio) */}
      <Box
        className={classes.header}
        style={collapsed
          ? {
            flexDirection: 'column',
            gap: 10,
            justifyContent: 'center',
            padding: '16px 0 12px',
          }
          : undefined}
      >
        {collapsed
          ? (collapsedLogo
            ? (
              <img
                src={collapsedLogo.src}
                alt={companyName || 'Logo'}
                className={clsx(
                  classes.brandIcon,
                  collapsedLogo.wide && classes.brandIconWide,
                )}
                onError={collapsedLogo.onError}
              />
            )
            : <Box className={classes.brandMark}>{brandInitial}</Box>)
          : (
            <Box className={classes.brand}>
              {brandLogo && !brandLogoError
                ? (
                  <img
                    src={brandLogo}
                    alt={companyName || 'Logo'}
                    className={clsx(
                      classes.brandLogo,
                      isPinpeo && classes.brandLogoPinpeo,
                    )}
                    onError={() => setBrandLogoError(true)}
                  />
                )
                : (
                  <>
                    <Box className={classes.brandMark}>{brandInitial}</Box>
                    {companyName && (
                      <Typography className={classes.brandName}>
                        {companyName}
                      </Typography>
                    )}
                  </>
                )}
            </Box>
          )}
        <IconButton
          size="small"
          className={classes.collapseButton}
          onClick={onToggleCollapse}
        >
          {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Box>

      {/* Selector global de agregador */}
      <Box className={classes.selectorSection} style={collapsed ? { padding: '4px 10px 14px' } : undefined}>
        {!collapsed && <Typography className={classes.selectorLabel}>Agregador</Typography>}

        <Tooltip
          title={collapsed ? (selectedAggregator?.name || 'Elegir agregador') : ''}
          placement="right"
        >
          <button
            type="button"
            ref={selectorRef}
            className={clsx(
              classes.selectorButton,
              collapsed && classes.selectorButtonCollapsed,
            )}
            onClick={() => setSelectorOpen(true)}
          >
            <DeviceHubIcon
              style={{ fontSize: 17, color: theme.brand.accentOnDark, flexShrink: 0 }}
            />
            {!collapsed && (
              <>
                <span className={classes.selectorName}>
                  {selectedAggregator?.name || 'Elegir agregador'}
                </span>
                <UnfoldMoreIcon style={{ fontSize: 15, color: 'rgba(255,255,255,.5)' }} />
              </>
            )}
          </button>
        </Tooltip>

        <Menu
          anchorEl={selectorRef.current}
          open={selectorOpen}
          onClose={handleCloseSelector}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          PaperProps={{
            style: {
              width: 240,
              maxHeight: 360,
              borderRadius: 12,
              boxShadow: PINPEO.shadowPopover,
              border: `1px solid ${PINPEO.border}`,
            },
          }}
        >
          {/* Búsqueda por teclado (nombre o id) */}
          <Box px={1.5} pt={1} pb={0.5}>
            <TextField
              placeholder="Buscar agregador…"
              size="small"
              variant="outlined"
              fullWidth
              autoFocus
              value={selectorSearch}
              onChange={(e) => setSelectorSearch(e.target.value)}
              // Evita que el Menu capture las teclas para su navegación
              onKeyDown={(e) => e.stopPropagation()}
              inputProps={{ style: { fontSize: 13 } }}
            />
          </Box>

          {filteredAggregators.map((item) => {
            const active = String(item.id) === String(selectedAggregatorId);

            return (
              <MenuItem
                key={item.id}
                dense
                selected={active}
                onClick={() => {
                  setSelectedAggregatorId(item.id);
                  handleCloseSelector();
                }}
                style={{ fontSize: 13, fontWeight: active ? 600 : 400 }}
              >
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.name}
                </span>
                {active && (
                  <CheckIcon style={{ fontSize: 16, color: theme.brand.primary }} />
                )}
              </MenuItem>
            );
          })}
          {filteredAggregators.length === 0 && (
            <MenuItem disabled style={{ fontSize: 13 }}>
              {aggregators.length === 0
                ? 'Sin agregadores disponibles'
                : 'Sin resultados para esta búsqueda'}
            </MenuItem>
          )}
        </Menu>
      </Box>

      {/* Navegación */}
      <Box component="nav" className={classes.nav} style={collapsed ? { padding: '0 10px' } : undefined}>
        {NAV_SECTIONS.map((section) => {
          const items = filterNavItems(section.items).filter((item) => !item.reviewersOnly || isReviewer);
          if (!items.length) return null;

          return (
            <Box key={section.subheader}>
              {!collapsed && (
                <Typography className={classes.subheader}>
                  {section.subheader}
                </Typography>
              )}
              {collapsed && <Box style={{ height: 14 }} />}
              {items.map(renderNavItem)}
            </Box>
          );
        })}
      </Box>

      {/* Cuenta */}
      <AccountSection collapsed={collapsed} />
    </Box>
  );
}

Sidebar.propTypes = {
  collapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};

Sidebar.defaultProps = {
  collapsed: false,
  onToggleCollapse: () => {},
};

export default Sidebar;
